import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageLayout } from "../../components/page-layout";
import { OnboardingProgressBar } from "../../components/onboarding-progress-bar";
import { useOnboarding } from "../../providers/onboarding-provider";
import { useAuth0 } from "@auth0/auth0-react";
import { PageLoader } from "../../components/page-loader";
import { usePersonaInquiry } from "../../hooks/usePersonaInquiry";
import { FormShell } from "../../components/onyx/FormShell";
import { FormActions } from "../../components/onyx/Field";
import { Button } from "../../components/onyx/Button";
import {
  createPersonaReference,
  confirmVerification,
  updateInquiryId,
} from "../../services/backend.service";
import { QRCodeSVG } from "qrcode.react";

// Helper function to format dates without timezone conversion
const formatDateWithoutTimezone = (dateString) => {
  if (!dateString) return 'N/A';
  // Parse the date string directly without creating a Date object
  const [year, month, day] = dateString.split('-');
  return `${month}/${day}/${year}`;
};

export const VerificationPage = () => {
  const navigate = useNavigate();
  const {
    completeStep,
    stepStatuses,
    fetchOnboardingStatus,
    navigateToLatestStep,
    ONBOARDING_STEPS,
  } = useOnboarding();
  const [verificationData] = useState(null);
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const [autoOpened, setAutoOpened] = useState(false);

  // Service-call adapters: the hook is shape-stable on
  // ({ reference_id, inquiry_id }) payloads, so we shim the legacy
  // positional-argument helpers here. Using useCallback so the hook's
  // useEffect deps stay stable across renders.
  const fetchReference = useCallback(async () => {
    if (!isAuthenticated) {
      return { data: null, error: { message: 'User is not authenticated' } };
    }
    const token = await getAccessTokenSilently();
    return createPersonaReference(token);
  }, [getAccessTokenSilently, isAuthenticated]);

  const updateInquiry = useCallback(
    async ({ reference_id, inquiry_id }) => {
      const token = await getAccessTokenSilently();
      return updateInquiryId(token, reference_id, inquiry_id);
    },
    [getAccessTokenSilently],
  );

  const confirmInquiry = useCallback(
    async ({ reference_id, inquiry_id }) => {
      const token = await getAccessTokenSilently();
      return confirmVerification(token, reference_id, inquiry_id);
    },
    [getAccessTokenSilently],
  );

  // After confirm completes (whether auto-advanced or after the
  // participant finishes the iframe flow), bounce them to whatever
  // the onboarding engine says is next.
  const handleComplete = useCallback(async () => {
    const statusData = await fetchOnboardingStatus({ silent: true });
    if (statusData?.current_step && ONBOARDING_STEPS[statusData.current_step]) {
      navigate(ONBOARDING_STEPS[statusData.current_step].path);
    } else {
      navigateToLatestStep();
    }
  }, [fetchOnboardingStatus, ONBOARDING_STEPS, navigate, navigateToLatestStep]);

  const {
    reference,
    loading,
    error,
    open,
    setError,
  } = usePersonaInquiry({
    fetchReference,
    updateInquiry,
    confirmInquiry,
    onComplete: handleComplete,
  });

  // Match the legacy auto-resume behavior: if the backend handed us an
  // inquiry_id (and we're not short-circuiting via should_advance —
  // the hook handles that itself), launch Persona automatically once
  // per inquiry. Manual "Start Verification" still works for the
  // no-inquiry-yet path.
  useEffect(() => {
    if (
      !autoOpened
      && reference?.inquiry_id
      && !reference?.should_advance
    ) {
      setAutoOpened(true);
      open();
    }
  }, [reference, autoOpened, open]);

  const startVerification = () => {
    setError(null);
    open();
  };

  const handleContinue = async () => {
    await completeStep('VERIFY');
  };

  if (error) {
    return (
      <PageLayout>
        <div className="content-layout">
          <OnboardingProgressBar />
          <FormShell>
            <div className="error-message">
              <p>{error.message || error}</p>
            </div>
            <FormActions>
              <Button type="button" variant="primary" onClick={startVerification}>
                Retry Verification
              </Button>
            </FormActions>
          </FormShell>
        </div>
      </PageLayout>
    );
  }

  if (loading) {
    return (
      <PageLayout>
        <div className="content-layout">
          <OnboardingProgressBar />
          <PageLoader />
        </div>
      </PageLayout>
    );
  }

  const isVerified = stepStatuses?.VERIFY?.status === 'completed';

  return (
    <PageLayout>
      <div className="content-layout">
        <OnboardingProgressBar />
        <FormShell>
          <h1 id="page-title" className="content__title">
            Identity Verification
          </h1>

          {isVerified && verificationData ? (
            <>
              <p className="content__body">Your identity has been verified successfully.</p>
              <div className="verification-details">
                <h2>Verification Details</h2>
                <div className="detail-group">
                  <h3>Personal Information</h3>
                  <p>Name: {verificationData.first_name} {verificationData.middle_name || ''} {verificationData.last_name}</p>
                  <p>Date of Birth: {formatDateWithoutTimezone(verificationData.date_of_birth)}</p>
                </div>

                <div className="detail-group">
                  <h3>Address</h3>
                  <p>{verificationData.address_street_1}</p>
                  {verificationData.address_street_2 && <p>{verificationData.address_street_2}</p>}
                  <p>{verificationData.address_city}, {verificationData.address_state} {verificationData.address_postal_code}</p>
                </div>

                <div className="detail-group">
                  <h3>Government ID</h3>
                  <p>Type: {verificationData.id_type}</p>
                  <p>State: {verificationData.id_state}</p>
                  <p>ID Number: {verificationData.id_number}</p>
                  <p>Expiration: {formatDateWithoutTimezone(verificationData.id_expiration_date)}</p>
                </div>
              </div>
              <FormActions>
                <Button type="button" variant="primary" onClick={handleContinue}>
                  Continue
                </Button>
              </FormActions>
            </>
          ) : (
            <>
              <p className="content__body">
                Please verify your identity to complete enrollment. Our partner Persona will require you to take a picture of your government-issued ID and a selfie.
              </p>
              <FormActions>
                <Button type="button" variant="primary" onClick={startVerification}>
                  Start Verification
                </Button>
              </FormActions>
              <div className="verification-options">
                <div className="verification-option">
                  <h3>Want to use your phone camera?</h3>
                  <p>Scan this QR code and continue on your phone.</p>
                  <div className="qr-code-container">
                    <QRCodeSVG
                      value={`${window.location.origin}/verify`}
                      size={200}
                      level="H"
                      includeMargin={true}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </FormShell>
      </div>
    </PageLayout>
  );
};
